// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BiSolidPhoneCall,
  BiSolidXCircle,
  BiSolidEdit,
  BiSolidTrash,
  BiLinkAlt,
  BiLinkExternal,
  BiUndo,
} from 'react-icons/bi';
import { HeaderCell, BodyCell, DataTableLoading, DataTableEmpty } from '@hopdrive/sdk-ui';

import {
  formatDateTime,
  formatDuration,
  formatPhoneNumber,
  getLatestRidehail,
  getRideDurationMinutes,
  isFailed,
} from '../utils/guestRideUtils';
import GuestRideStatusBadge from '@components/Badges/GuestRideStatusBadge';
import GuestRideApprovalNote from './GuestRideApprovalNote';
import { guestRideActionsFor } from '../utils/rideActions';

import CancelRideModal from './CancelRideModal';
import DeleteRideModal from './DeleteRideModal';
import RevertRideModal from './RevertRideModal';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';
import CallRideModal from './CallRideModal';
import GuestRideModal from './GuestRideModal';
import GuestRideDetailsModal from './GuestRideDetailsModal';
import GuestLinkModal from './GuestLinkModal';
import GuestRideProviderMark from './GuestRideProviderMark';
import { useRooftop } from '@store';
import type { GuestRideSortBy, SortOrder } from '../stores/guestRideStore';

import { useTheme } from '@mui/material';

// TYPES ---------------------------------------------------------------- //

interface GuestRidesTableProps {
  rides: any[];
  loading: boolean;
  hasData: boolean;
  sortBy: GuestRideSortBy;
  sortOrder: SortOrder;
  onSortChange: (sortBy: GuestRideSortBy, sortOrder: SortOrder) => void;
  onClearFilters: () => void;
  highlightedRideId?: number | null;
  onHighlightClear?: () => void;
}

// HELPERS ---------------------------------------------------------------- //

const COL_COUNT = 7;

// COMPONENT ---------------------------------------------------------------- //

const GuestRidesTable: React.FC<GuestRidesTableProps> = ({
  rides,
  loading,
  hasData,
  sortBy,
  sortOrder,
  onSortChange,
  onClearFilters,
  highlightedRideId,
  onHighlightClear,
}) => {
  const theme = useTheme();
  // Managing a ride (cancel, edit, delete, revert) is gated on the Guest Rides product's manage
  // capability; a read_only product shows rides for viewing only.
  const canManage = capabilityGate(useProductEntry('guest-rides'), 'manageExisting').kind === 'render';
  React.useEffect(() => {
    if (highlightedRideId && onHighlightClear) {
      const timer = setTimeout(() => onHighlightClear(), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedRideId, onHighlightClear]);

  const rooftop = useRooftop();

  const [modalState, setModalState] = React.useState<{
    type: 'cancel' | 'call' | 'retry' | 'edit' | 'delete' | 'revert' | 'share' | 'details' | null;
    ride: any;
    isRetry: boolean;
  }>({ type: null, ride: null, isRetry: false });

  const closeModal = () => setModalState({ type: null, ride: null, isRetry: false });

  const handleSort = (column: string) => () => {
    const isSameColumn = sortBy === column;
    const newOrder = isSameColumn && sortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange(column as GuestRideSortBy, newOrder);
  };

  return (
    <>
      <TableContainer>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              {/* Guest */}
              <HeaderCell>
                <TableSortLabel
                  active={sortBy === 'guest_name'}
                  direction={sortBy === 'guest_name' ? sortOrder : 'asc'}
                  onClick={handleSort('guest_name')}
                >
                  Guest
                </TableSortLabel>
              </HeaderCell>

              {/* Route */}
              <HeaderCell>
                <TableSortLabel
                  active={sortBy === 'pickup_address'}
                  direction={sortBy === 'pickup_address' ? sortOrder : 'asc'}
                  onClick={handleSort('pickup_address')}
                >
                  Route
                </TableSortLabel>
              </HeaderCell>

              {/* Date */}
              <HeaderCell>
                <TableSortLabel
                  active={sortBy === 'created_at'}
                  direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                  onClick={handleSort('created_at')}
                >
                  Date
                </TableSortLabel>
              </HeaderCell>

              {/* Status */}
              <HeaderCell>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortOrder : 'asc'}
                  onClick={handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </HeaderCell>

              {/* Provider */}
              <HeaderCell>Provider</HeaderCell>

              {/* Duration */}
              <HeaderCell>Duration</HeaderCell>

              {/* Actions */}
              <HeaderCell sx={{ textAlign: 'right' }}>Actions</HeaderCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <DataTableLoading colSpan={COL_COUNT} />
            ) : rides.length === 0 ? (
              <DataTableEmpty
                colSpan={COL_COUNT}
                variant={hasData ? 'no-matches' : 'zero-data'}
                message={hasData ? 'No guest rides match your filters' : 'No guest rides yet'}
                onClearFilters={hasData ? onClearFilters : undefined}
              />
            ) : (
              rides.map((ride: any, index: number) => {
                const ridehail = getLatestRidehail(ride);
                const provider = ridehail?.provider || ride?.provider;
                const failed = isFailed(ride);
                const actions = guestRideActionsFor(ride, canManage);
                const canEdit = actions.edit;
                const canDelete = actions.delete;
                const canRevert = actions.revert;
                const canCancel = actions.cancel || actions.cancelRequest;
                const isLastRow = index === rides.length - 1;
                const isHighlighted = highlightedRideId === ride?.id;

                return (
                  <TableRow
                    key={ride.id}
                    hover
                    onClick={() => setModalState({ type: 'details', ride, isRetry: false })}
                    sx={{
                      cursor: 'pointer',
                      ...(failed ? { backgroundColor: 'rgba(255,32,64,0.06)' } : {}),
                      ...(isLastRow ? { '& td': { borderBottom: 'none' } } : {}),
                      ...(isHighlighted
                        ? {
                            animation: 'highlightRowPulse 1.5s ease-in-out 2',
                            '@keyframes highlightRowPulse': {
                              '0%, 100%': { backgroundColor: 'transparent' },
                              '50%': { backgroundColor: theme.palette.primary.lighter },
                            },
                          }
                        : {}),
                    }}
                  >
                    <BodyCell>
                      <Typography variant='body2' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {ride.guest_name || '-'}
                      </Typography>
                      <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {formatPhoneNumber(ride.guest_phone)}
                      </Typography>
                    </BodyCell>
                    <BodyCell>
                      <Typography variant='body2' sx={{ lineHeight: 1.3 }}>
                        {ride.pickup_address || '-'}
                      </Typography>
                      <Typography variant='body2' sx={{ lineHeight: 1.3 }}>
                        ↪ {ride.dropoff_address || '-'}
                      </Typography>
                    </BodyCell>
                    <BodyCell>{formatDateTime(ride.created_at)}</BodyCell>
                    <BodyCell>
                      <GuestRideStatusBadge status={ride.status} />
                      <Box onClick={e => e.stopPropagation()} sx={{ mt: 0.5 }}>
                        <GuestRideApprovalNote ride={ride} canResend={actions.resend} dense />
                      </Box>
                    </BodyCell>
                    <BodyCell>
                      {(() => {
                        if (!provider) return '-';
                        const lp = provider.toLowerCase();
                        return lp.includes('uber') || lp.includes('lyft') ? (
                          <GuestRideProviderMark provider={provider} />
                        ) : (
                          '-'
                        );
                      })()}
                    </BodyCell>
                    <BodyCell>
                      <Typography variant='body2' sx={{ lineHeight: 1.3 }}>
                        {formatDuration(getRideDurationMinutes(ride))}
                      </Typography>
                    </BodyCell>
                    <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box
                        onClick={e => e.stopPropagation()}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                      >
                        {actions.call && (
                          <Button
                            size='small'
                            variant='contained'
                            color='primary'
                            startIcon={<BiSolidPhoneCall size={16} />}
                            onClick={() =>
                              setModalState(
                                actions.call === 'retry-edit'
                                  ? { type: 'retry', ride, isRetry: true }
                                  : { type: 'call', ride, isRetry: actions.call === 'retry' }
                              )
                            }
                            sx={{ mr: 1 }}
                          >
                            {actions.call === 'call' ? 'Call Ride' : 'Retry Ride'}
                          </Button>
                        )}

                        {canCancel && (
                          <Button
                            size='small'
                            variant='contained'
                            color='error'
                            startIcon={<BiSolidXCircle size={16} />}
                            onClick={() => setModalState({ type: 'cancel', ride, isRetry: false })}
                          >
                            {actions.cancelRequest ? 'Cancel Request' : 'Cancel Ride'}
                          </Button>
                        )}

                        <Tooltip title='Share guest link'>
                          <IconButton
                            size='small'
                            onClick={() => setModalState({ type: 'share', ride, isRetry: false })}
                          >
                            <BiLinkAlt size={20} />
                          </IconButton>
                        </Tooltip>

                        {!canEdit && (
                          <Tooltip title='View details'>
                            <IconButton
                              size='small'
                              onClick={() => setModalState({ type: 'details', ride, isRetry: false })}
                            >
                              <BiLinkExternal size={20} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {canEdit && (
                          <Tooltip title='Edit ride'>
                            <IconButton
                              size='small'
                              onClick={() => setModalState({ type: 'edit', ride, isRetry: false })}
                            >
                              <BiSolidEdit size={20} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {canDelete && (
                          <Tooltip title='Delete ride'>
                            <IconButton
                              size='small'
                              onClick={() => setModalState({ type: 'delete', ride, isRetry: false })}
                            >
                              <BiSolidTrash size={20} />
                            </IconButton>
                          </Tooltip>
                        )}

                        {canRevert && (
                          <Button
                            size='small'
                            variant='contained'
                            color='primary'
                            startIcon={<BiUndo size={16} />}
                            onClick={() => setModalState({ type: 'revert', ride, isRetry: false })}
                          >
                            Revert
                          </Button>
                        )}
                      </Box>
                    </BodyCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CallRideModal
        open={modalState.type === 'call'}
        onClose={closeModal}
        ride={modalState.ride}
        isRetry={modalState.isRetry}
      />

      <GuestRideModal open={modalState.type === 'edit'} onClose={closeModal} ride={modalState.ride} />

      <GuestRideModal open={modalState.type === 'retry'} onClose={closeModal} ride={modalState.ride} isRetry />

      <GuestRideDetailsModal
        open={modalState.type === 'details'}
        onClose={closeModal}
        ride={modalState.ride}
      />

      <CancelRideModal open={modalState.type === 'cancel'} onClose={closeModal} ride={modalState.ride} />

      <DeleteRideModal open={modalState.type === 'delete'} onClose={closeModal} ride={modalState.ride} />

      <RevertRideModal open={modalState.type === 'revert'} onClose={closeModal} ride={modalState.ride} />

      <GuestLinkModal
        open={modalState.type === 'share'}
        onClose={closeModal}
        entityId={modalState.ride?.id}
        entityType='guestride'
        templateType='guestride.link.shared'
        rooftopName={rooftop?.name ?? undefined}
        showQrMode={false}
        defaultCustomerName={modalState.ride?.guest_name ?? undefined}
        defaultCustomerPhone={modalState.ride?.guest_phone ?? undefined}
        title='Share Guest Link'
      />
    </>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default GuestRidesTable;
